// frontend/src/widgets/JobFormWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-08
// Updated:      2026-10-08 10:22 BST
//
// Description:
//   Job creation wizard with client, taxonomy, schedule, documents, expenses and review.
//   Supports two planning modes:
//   • Exact schedule mode via custom date + quarter-hour time selects
//   • Week commencing mode via nullable `week_commencing`
//   Includes primary/co-resource (co optional), location persistence fixes, and
//   evidence-only job_ref uniqueness checker.
//
// Change Log (BST)
//   2026-10-20 08:27 BST: Use each Work Package draft location rather than parent Job location.
// - 2026-10-08 10:22 Fixed JSX wrapper regression in historic Week Commencing control; no sizing/RGL changes.
// - 2026-10-08 10:20 Added unrestricted historic/future Monday selection for Week Commencing mode; retained accepted RGL sizing contract.
// - 2026-10-08 09:31 Restored the exact pre-Work-Package/Git sizing contract; WP additions retained with no desktop sizing changes.
// - 2026-10-07 15:42 Fixed desktop Job Form grid minimum geometry and replaced ambiguous WP buttons with shared explicit selector/binding preview.
// - 10:12 Replaced native datetime-local with custom date + quarter-hour time selects (00/15/30/45 only).
// - 10:18 Auto-defaults to the *next* quarter-hour for Start; End defaults +1 hour (rounded).
// - 10:26 Location persistence retained with PUT→PATCH→POST(/location) fallback.
// - 10:34 Job Ref uniqueness check is evidence-only (no false positives). Create button disables only on real duplicate.
// - 2026-09-29 Job Ref check now uses canonical authenticated GET /jobs/ref/check only; legacy probe chain removed.
// - 10:45 Co-resource kept as optional dropdown. Assignment POST posts both primary + co if set.
// - 13:35 Added week commencing planning mode with Monday dropdown for next 3 months.
// - 13:35 Exact date/time fields now hide when week commencing mode is enabled.
// - 13:35 Resource assignment is disabled in week commencing mode to avoid writing fake schedule data.
// - 13:35 Review panel and create payload now persist `week_commencing` correctly.
// - 2026-09-02 Q-14A: Customer list now loads all pages.
// - 2026-09-02 Q-14A: Added tenant-visible Opportunity and Quote selectors to Basics.
// - 2026-09-02 Q-14A: Commercial selectors are customer-scoped; Quote selection derives
//   its authoritative Opportunity where present; create payload persists both IDs.
// - 2026-09-02 Q-14A: Review step now shows Opportunity and Quote provenance.
// - 2026-09-08 Mobile: Preserved the desktop six-step wizard while making all
//   steps, controls, review content and footer actions responsive below 768px.
// - 2026-09-08 Mobile: Added mobile ReactGridLayout host-height synchronisation
//   so the complete active wizard step remains reachable without a resize handle.
// - 2026-09-08 Security: Removed frontend-supplied tenant_id and processed_by
//   from job creation, removed tenant_id injection from queued expenses, and
//   removed the legacy tenantId prop from DocumentUploadAccordion. Tenant/actor
//   context is resolved by the authenticated backend.

import React, { useEffect, useMemo, useState, useRef } from "react";
import axiosInstance from "../api/axiosInstance";
import WorkPackageComposer from "../components/WorkPackageComposer.jsx";
import { useTheme } from "../context/ThemeContext";
import DocumentUploadAccordion from "../components/DocumentUploadAccordion.jsx";
import ExpenseCreateAccordion from "../components/ExpenseCreateAccordion.jsx";
import LocationEditModal from "../components/LocationEditModal.jsx";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

// ---------- Small helpers ----------
const inputCls =
  "w-full border border-gray-300 rounded px-3 py-2 min-h-[44px] md:min-h-0 text-base md:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent/40";
const selectCls = inputCls;
const areaCls = `${inputCls} min-h-[140px]`;
const fieldLabelCls = "text-xs text-gray-600 mb-1 block";
const cardCls =
  "rounded-lg border border-gray-200 shadow-sm bg-white overflow-visible md:overflow-hidden flex flex-col min-w-0";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const isoNoMs = (v) => {
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().replace(/\.\d{3}Z$/, "Z");
  } catch {
    return "";
  }
};

// FIX: accept array OR {items:[...]} shapes
const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.quotes)) return data.quotes;
  if (Array.isArray(data.opportunities)) return data.opportunities;
  return [];
};

// FIX: customer sort
const sortCustomersAsc = (rows) => {
  const arr = Array.isArray(rows) ? [...rows] : [];
  arr.sort((a, b) => {
    const an = (a?.name || "").toString();
    const bn = (b?.name || "").toString();
    return an.localeCompare(bn, undefined, { sensitivity: "base" });
  });
  return arr;
};

// FIX: contact sort
const sortContactsAsc = (rows) => {
  const arr = Array.isArray(rows) ? [...rows] : [];
  arr.sort((a, b) => {
    const al = (a?.last_name || "").toString();
    const bl = (b?.last_name || "").toString();
    const lc = al.localeCompare(bl, undefined, { sensitivity: "base" });
    if (lc) return lc;
    const af = (a?.first_name || "").toString();
    const bf = (b?.first_name || "").toString();
    const fc = af.localeCompare(bf, undefined, { sensitivity: "base" });
    if (fc) return fc;
    const ae = (a?.email || "").toString();
    const be = (b?.email || "").toString();
    return ae.localeCompare(be, undefined, { sensitivity: "base" });
  });
  return arr;
};

// FIX: fetch all pages for paginated endpoints
const fetchAllPaged = async (path, { baseURL = "/api", limit = 200, maxPages = 50 } = {}) => {
  const out = [];
  let offset = 0;

  for (let page = 0; page < maxPages; page++) {
    const sep = path.includes("?") ? "&" : "?";
    const url = `${path}${sep}limit=${limit}&offset=${offset}`;
    const { data } = await axiosInstance.get(url, { baseURL });

    const items = listFrom(data);
    out.push(...items);

    const total = Number(data?.total);
    const pageLimit = Number(data?.limit) || limit;

    if (!Number.isFinite(total) || total <= 0) break;

    offset += pageLimit;
    if (out.length >= total) break;
    if (items.length === 0) break;
  }

  return out;
};

// "YYYY-MM-DDTHH:mm" <-> (date "YYYY-MM-DD", time "HH:mm")
const combineLocalDateTime = (dateStr, timeStr) => {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return "";
  if (!timeStr || !/^\d{2}:\d{2}$/.test(timeStr)) return `${dateStr}T00:00`;
  return `${dateStr}T${timeStr}`;
};

const splitLocalDateTime = (dtStr) => {
  if (!dtStr || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dtStr)) {
    return { date: "", time: "" };
  }
  const [date, time] = dtStr.split("T");
  return { date, time };
};

// Quarter-hour rounding helpers
const roundMinutesToQuarter = (mins) => clamp(Math.round(mins / 15) * 15, 0, 59);
const pad2 = (n) => String(n).padStart(2, "0");

// Get next quarter-hour as "HH:mm" local
const nextQuarterLocalTime = (d = new Date()) => {
  const hh = d.getHours();
  const mm = d.getMinutes();
  const rounded = roundMinutesToQuarter(mm);
  let rh = hh;
  let rm = rounded;
  if (rounded <= mm) {
    rm = (rounded + 15) % 60;
    if (rm === 0) rh = (hh + 1) % 24;
  }
  return `${pad2(rh)}:${pad2(rm)}`;
};

// Generate "HH:mm" options at 15-minute intervals (00:00 .. 23:45)
const quarterTimes = Array.from({ length: 24 * 4 }, (_, i) => {
  const hh = Math.floor(i / 4);
  const mm = (i % 4) * 15;
  return `${pad2(hh)}:${pad2(mm)}`;
});

// ---- Week commencing helpers ----
const DEFAULT_USE_WEEK_COMMENCING = true;

const formatDateOnlyLocal = (d) => {
  const year = d.getFullYear();
  const month = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${year}-${month}-${day}`;
};

const mondayOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 = Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

const addDaysLocal = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const buildWeekCommencingOptions = (monthsAhead = 3) => {
  const today = new Date();
  const startMonday = mondayOfWeek(today);
  const endDate = new Date(today);
  endDate.setMonth(endDate.getMonth() + monthsAhead);

  const options = [];
  let cursor = new Date(startMonday);

  while (cursor <= endDate) {
    options.push({
      value: formatDateOnlyLocal(cursor),
      label: `Mon ${cursor.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}`,
    });
    cursor = addDaysLocal(cursor, 7);
  }

  return options;
};

const DEFAULT_FORM = {
  customer_id: "",
  contact_id: "",
  opportunity_id: "",
  quote_id: "",
  tags: [],
  job_type_id: "",
  job_subtype_id: "",
  job_subtype2_id: "",
  priority: "Normal",
  planned_start: "", // "YYYY-MM-DDTHH:mm"
  planned_end: "", // "YYYY-MM-DDTHH:mm"
  week_commencing: "",
  use_week_commencing: DEFAULT_USE_WEEK_COMMENCING,
  location_id: "",
  resource_id: "",
  co_resource_id: "",
  notes: "",
  job_ref: "",
};

const HeaderBar = ({ title, right }) => (
  <div className="bg-accent text-white px-3 md:px-4 py-2 flex items-center justify-between gap-3 min-w-0">
    <span className="text-sm font-semibold min-w-0 break-words">{title}</span>
    <div className="shrink-0">{right}</div>
  </div>
);

const StepDots = ({ total, current, theme }) => (
  <div className="flex items-center gap-1.5">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        title={`Step ${i + 1}`}
        style={{
          width: 9,
          height: 9,
          borderRadius: 999,
          background: i === current ? theme?.accent_color || "#ff7a00" : "#c5c7cd",
        }}
      />
    ))}
  </div>
);

const friendlyResourceName = (r) => {
  if (!r || typeof r !== "object") return "";
  const candidates = [
    r.display_name,
    r.name,
    [r.first_name, r.last_name].filter(Boolean).join(" ").trim(),
    r.full_name,
    r.identifier,
    r.username,
    r.email,
    r.code,
    r.external_id,
  ].filter(Boolean);
  return String(candidates[0] || `Resource ${r.id ?? ""}`).trim();
};

const byFriendlyResource = (a, b) =>
  friendlyResourceName(a).localeCompare(friendlyResourceName(b), undefined, {
    sensitivity: "base",
  });

// ---- Lists ----
const useLists = (customerId, jobTypeId, jobSubtypeId) => {
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [subtypes2, setSubtypes2] = useState([]);

  // customers + types
  useEffect(() => {
    fetchAllPaged("/customers", { baseURL: "/api", limit: 200 })
      .then((rows) => setCustomers(sortCustomersAsc(rows)))
      .catch(() => setCustomers([]));

    axiosInstance
      .get("/job-types?sort=name:asc", { baseURL: "/api" })
      .then((r) => setTypes(listFrom(r.data)))
      .catch(() => setTypes([]));
  }, []);

  // contacts (depends on customer)
  useEffect(() => {
    const load = async () => {
      if (!customerId) {
        setContacts([]);
        return;
      }
      const cid = encodeURIComponent(customerId);

      const applyFilter = (rows) => {
        const arr = Array.isArray(rows) ? rows : [];
        return arr.filter(
          (x) =>
            String(x.customer_id) === String(customerId) ||
            String(x.client_id) === String(customerId) ||
            String(x.organisation_id) === String(customerId)
        );
      };

      try {
        const scoped = await fetchAllPaged(`/contacts?customer_id=${cid}`, { baseURL: "/api", limit: 250 });
        const filtered = sortContactsAsc(applyFilter(scoped));
        setContacts(filtered);
        return;
      } catch {
        // fall through
      }

      try {
        const scoped2 = await fetchAllPaged(`/contacts?client_id=${cid}`, { baseURL: "/api", limit: 250 });
        const filtered = sortContactsAsc(applyFilter(scoped2));
        setContacts(filtered);
        return;
      } catch {
        // fall through
      }

      try {
        const all = await fetchAllPaged(`/contacts`, { baseURL: "/api", limit: 250 });
        const filtered = sortContactsAsc(applyFilter(all));
        setContacts(filtered);
      } catch {
        setContacts([]);
      }
    };
    load();
  }, [customerId]);

  // subtypes (depends on type)
  useEffect(() => {
    if (!jobTypeId) {
      setSubtypes([]);
      return;
    }
    axiosInstance
      .get(`/job-subtypes?job_type_id=${jobTypeId}&sort=name:asc`, {
        baseURL: "/api",
      })
      .then((r) => setSubtypes(listFrom(r.data)))
      .catch(() => setSubtypes([]));
  }, [jobTypeId]);

  // subtypes2 (depends on subtype)
  useEffect(() => {
    if (!jobSubtypeId) {
      setSubtypes2([]);
      return;
    }
    axiosInstance
      .get(`/job-subtypes2?job_subtype_id=${jobSubtypeId}&sort=name:asc`, {
        baseURL: "/api",
      })
      .then((r) => setSubtypes2(listFrom(r.data)))
      .catch(() => setSubtypes2([]));
  }, [jobSubtypeId]);

  return { customers, contacts, types, subtypes, subtypes2 };
};

const TOTAL_STEPS = 6;

const JobFormWidget = () => {
  const { theme } = useTheme();
  const rootRef = useRef(null);

  const weekCommencingOptions = useMemo(() => buildWeekCommencingOptions(3), []);

  // Desktop sizing is owned exclusively by react-grid-layout.
  //
  // IMPORTANT:
  //   Do not write height/minHeight to the surrounding .react-grid-item from this widget.
  //   The previous mobile synchroniser captured the grid height at mount and later wrote
  //   that stale value back from a ResizeObserver callback. Once the Work Package controls
  //   made the form content change dynamically, those callbacks could run during/after a
  //   desktop grid resize and snap the host back to the captured height.
  //
  // JobsPage already renders mobile widgets outside react-grid-layout in a natural
  // single-column flow, so JobFormWidget does not need any host-height synchronisation.


  const [form, setForm] = useState({
    ...DEFAULT_FORM,
    week_commencing: weekCommencingOptions[0]?.value || "",
  });
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [containsWorkPackages, setContainsWorkPackages] = useState(false);
  const [workPackageDrafts, setWorkPackageDrafts] = useState([]);


  const { customers, contacts, types, subtypes, subtypes2 } = useLists(
    form.customer_id || null,
    form.job_type_id || null,
    form.job_subtype_id || null
  );

  // Q-14A commercial provenance lists.
  const [opportunities, setOpportunities] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [commercialLoading, setCommercialLoading] = useState(false);
  const [commercialError, setCommercialError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadCommercial = async () => {
      setCommercialLoading(true);
      setCommercialError("");

      const [opportunityResult, quoteResult] = await Promise.allSettled([
        axiosInstance.get("/opportunities?limit=500&offset=0", { baseURL: "/api" }),
        axiosInstance.get("/quotes?limit=1000&offset=0&sort=updated_at:desc", { baseURL: "/api" }),
      ]);

      if (cancelled) return;

      if (opportunityResult.status === "fulfilled") {
        setOpportunities(listFrom(opportunityResult.value?.data));
      } else {
        setOpportunities([]);
      }

      if (quoteResult.status === "fulfilled") {
        setQuotes(listFrom(quoteResult.value?.data));
      } else {
        setQuotes([]);
      }

      const failed = [];
      if (opportunityResult.status === "rejected") failed.push("Opportunities");
      if (quoteResult.status === "rejected") failed.push("Quotes");
      setCommercialError(
        failed.length ? `${failed.join(" and ")} could not be loaded.` : ""
      );
      setCommercialLoading(false);
    };

    loadCommercial();

    return () => {
      cancelled = true;
    };
  }, []);

  const customerScopedOpportunities = useMemo(() => {
    if (!form.customer_id) return [];
    return [...opportunities]
      .filter((o) => String(o?.customer_id ?? "") === String(form.customer_id))
      .sort((a, b) =>
        String(a?.title || a?.name || `Opportunity ${a?.id ?? ""}`).localeCompare(
          String(b?.title || b?.name || `Opportunity ${b?.id ?? ""}`),
          undefined,
          { sensitivity: "base" }
        )
      );
  }, [opportunities, form.customer_id]);

  const customerScopedQuotes = useMemo(() => {
    if (!form.customer_id) return [];

    return [...quotes]
      .filter((q) => String(q?.customer_id ?? "") === String(form.customer_id))
      .filter((q) => {
        if (!form.opportunity_id) return true;
        return String(q?.opportunity_id ?? "") === String(form.opportunity_id);
      })
      .sort((a, b) => {
        const ad = new Date(a?.updated_at || a?.created_at || 0).getTime();
        const bd = new Date(b?.updated_at || b?.created_at || 0).getTime();
        if (ad !== bd) return bd - ad;
        return Number(b?.id || 0) - Number(a?.id || 0);
      });
  }, [quotes, form.customer_id, form.opportunity_id]);

  const handleCustomerChange = (customerId) => {
    setForm((f) => ({
      ...f,
      customer_id: customerId,
      contact_id: "",
      opportunity_id: "",
      quote_id: "",
    }));
  };

  const handleOpportunityChange = (opportunityId) => {
    setForm((f) => {
      const currentQuote = quotes.find(
        (q) => String(q?.id) === String(f.quote_id || "")
      );
      const quoteStillCompatible =
        !currentQuote ||
        !currentQuote?.opportunity_id ||
        String(currentQuote.opportunity_id) === String(opportunityId || "");

      return {
        ...f,
        opportunity_id: opportunityId,
        quote_id: quoteStillCompatible ? f.quote_id : "",
      };
    });
  };

  const handleQuoteChange = (quoteId) => {
    setForm((f) => {
      if (!quoteId) return { ...f, quote_id: "" };

      const selectedQuote = quotes.find(
        (q) => String(q?.id) === String(quoteId)
      );

      if (!selectedQuote) return { ...f, quote_id: quoteId };

      return {
        ...f,
        customer_id: selectedQuote?.customer_id
          ? String(selectedQuote.customer_id)
          : f.customer_id,
        contact_id:
          selectedQuote?.customer_id &&
          String(selectedQuote.customer_id) !== String(f.customer_id)
            ? ""
            : f.contact_id,
        opportunity_id: selectedQuote?.opportunity_id
          ? String(selectedQuote.opportunity_id)
          : "",
        quote_id: quoteId,
      };
    });
  };

  // lists for step 2
  const [resources, setResources] = useState([]);
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/resources", { baseURL: "/api" })
      .then((r) => setResources(listFrom(r.data)))
      .catch(() => setResources([]));
    axiosInstance
      .get("/locations", { baseURL: "/api" })
      .then((r) => setLocations(listFrom(r.data)))
      .catch(() => setLocations([]));
  }, []);

  const sortedResources = useMemo(() => {
    const rows = Array.isArray(resources) ? [...resources] : [];
    rows.sort(byFriendlyResource);
    return rows;
  }, [resources]);

  const canNext = useMemo(() => {
    if (step === 0) return !!form.customer_id;
    if (step === 1) return !!form.job_type_id;
    if (step === 2 && form.use_week_commencing) return !!form.week_commencing;
    return true;
  }, [step, form]);

  // docs & expenses
  const [docTags, setDocTags] = useState([]);
  const [expenseDrafts, setExpenseDrafts] = useState([]);
  const [customerRef, setCustomerRef] = useState("");

  useEffect(() => {
    const run = async () => {
      setCustomerRef("");
      if (!form.customer_id) return;
      try {
        const { data } = await axiosInstance.get(
          `/customers/${encodeURIComponent(form.customer_id)}`,
          { baseURL: "/api" }
        );
        if (data?.internal_reference) {
          setCustomerRef(String(data.internal_reference));
        }
      } catch {}
    };
    run();
  }, [form.customer_id]);

  // ---- Job Ref live uniqueness check (evidence-only, debounced) ----
  const reqSeq = useRef(0);
  const [jobRefCheck, setJobRefCheck] = useState({
    status: "idle", // idle | checking | ok | unknown
    duplicate: false, // true only with explicit positive evidence
    id: null,
    msg: "",
    probedAny: false,
  });
  const norm = (s) => (s || "").trim();

  useEffect(() => {
    const ref = norm(form.job_ref);
    if (!ref) {
      setJobRefCheck({
        status: "idle",
        duplicate: false,
        id: null,
        msg: "",
        probedAny: false,
      });
      return;
    }

    const mySeq = ++reqSeq.current;
    setJobRefCheck((s) => ({
      ...s,
      status: "checking",
      msg: "",
      duplicate: false,
      id: null,
      probedAny: false,
    }));

    const check = async () => {
      try {
        const { data } = await axiosInstance.get("/jobs/ref/check", {
          baseURL: "/api",
          params: { job_ref: ref },
        });

        if (reqSeq.current !== mySeq) return;

        const duplicate = data?.exists === true;
        const id = data?.id ?? null;

        setJobRefCheck({
          status: "ok",
          duplicate,
          id,
          msg: duplicate
            ? `Job ref already exists${id ? ` (ID ${id})` : ""}.`
            : "",
          probedAny: true,
        });
      } catch (err) {
        if (reqSeq.current !== mySeq) return;

        console.warn("[JobFormWidget] Job Ref uniqueness check failed", err?.message);
        setJobRefCheck({
          status: "unknown",
          duplicate: false,
          id: null,
          msg: "",
          probedAny: false,
        });
      }
    };

    const t = setTimeout(check, 400);
    return () => clearTimeout(t);
  }, [form.job_ref]);

  const resetAll = () => {
    setForm({
      ...DEFAULT_FORM,
      week_commencing: weekCommencingOptions[0]?.value || "",
    });
    setStep(0);
    setContainsWorkPackages(false);
    setWorkPackageDrafts([]);
    setErrorText("");
    setOkMsg("");
    setCustomerRef("");
    setDocTags([]);
    setExpenseDrafts([]);
    setJobRefCheck({ status: "idle", duplicate: false, id: null, msg: "", probedAny: false });
    // also clear local date/time ui
    setStartDate("");
    setStartTime("");
    setEndDate("");
    setEndTime("");
  };

  const formatValidation = (data) => {
    const det = data?.detail;
    if (Array.isArray(det)) {
      return det
        .map((d) => {
          const loc = Array.isArray(d.loc) ? d.loc.slice(1).join(".") : "field";
          return `${loc}: ${d.msg}`;
        })
        .join("; ");
    }
    if (typeof det === "string") return det;
    try {
      return JSON.stringify(data);
    } catch {
      return "Validation failed.";
    }
  };

  // ----------------- Custom Date + Quarter-Hour Time UI state -----------------
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");

  // Initialise sensible defaults (next quarter for start; +1h for end)
  useEffect(() => {
    if (!startDate && !startTime && !endDate && !endTime) {
      const now = new Date();
      const startT = nextQuarterLocalTime(now);
      const startD = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
      const endObj = new Date(now.getTime() + 60 * 60 * 1000);
      const endT = nextQuarterLocalTime(endObj);
      const endD = `${endObj.getFullYear()}-${pad2(endObj.getMonth() + 1)}-${pad2(endObj.getDate())}`;

      setStartDate(startD);
      setStartTime(startT);
      setEndDate(endD);
      setEndTime(endT);

      setForm((f) => ({
        ...f,
        planned_start: combineLocalDateTime(startD, startT),
        planned_end: combineLocalDateTime(endD, endT),
      }));
    }
  }, [startDate, startTime, endDate, endTime]);

  // If planned_* is set externally, reflect into UI
  useEffect(() => {
    if (form.planned_start) {
      const { date, time } = splitLocalDateTime(form.planned_start);
      if (date && time) {
        setStartDate(date);
        setStartTime(time);
      }
    }
    if (form.planned_end) {
      const { date, time } = splitLocalDateTime(form.planned_end);
      if (date && time) {
        setEndDate(date);
        setEndTime(time);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When user changes date/time pieces, update form.planned_*
  const onStartDateChange = (v) => {
    setStartDate(v);
    setForm((f) => ({ ...f, planned_start: combineLocalDateTime(v, startTime) }));
  };
  const onStartTimeChange = (v) => {
    setStartTime(v);
    setForm((f) => ({ ...f, planned_start: combineLocalDateTime(startDate, v) }));
  };
  const onEndDateChange = (v) => {
    setEndDate(v);
    setForm((f) => ({ ...f, planned_end: combineLocalDateTime(v, endTime) }));
  };
  const onEndTimeChange = (v) => {
    setEndTime(v);
    setForm((f) => ({ ...f, planned_end: combineLocalDateTime(endDate, v) }));
  };

  const onUseWeekCommencingChange = (checked) => {
    setForm((f) => ({
      ...f,
      use_week_commencing: checked,
      week_commencing: checked ? (f.week_commencing || weekCommencingOptions[0]?.value || "") : "",
      resource_id: checked ? "" : f.resource_id,
      co_resource_id: checked ? "" : f.co_resource_id,
    }));
  };

  // ---------------------------------------------------------------------------

  // Build the list of resource IDs to assign (primary + co, unique, numbers only)
  const buildResourceIdList = () => {
    const ids = [];
    if (form.resource_id) ids.push(Number(form.resource_id));
    if (form.co_resource_id && form.co_resource_id !== form.resource_id) {
      ids.push(Number(form.co_resource_id));
    }
    return Array.from(new Set(ids)).filter(Boolean);
  };

  const createAssignmentsIfProvided = async (jobId) => {
    if (form.use_week_commencing) return;

    const starts_at = isoNoMs(form.planned_start);
    const ends_at = isoNoMs(form.planned_end);
    if (!starts_at || !ends_at) return;

    const ids = buildResourceIdList();
    if (!ids.length) return;

    const bodyFor = (rid) => ({
      resource_id: rid,
      starts_at,
      ends_at,
      status: "assigned",
    });

    try {
      await Promise.allSettled(
        ids.map((rid) =>
          axiosInstance.post(`/jobs/${jobId}/resources`, bodyFor(rid), {
            baseURL: "/api",
            headers: { "Content-Type": "application/json" },
          })
        )
      );
    } catch {
      /* best-effort */
    }
  };

  const ensureLocationPersisted = async (jobId, locId) => {
    if (!jobId || !locId) return;
    try {
      await axiosInstance.put(
        `/jobs/${encodeURIComponent(jobId)}`,
        { location_id: Number(locId) },
        { baseURL: "/api", headers: { "Content-Type": "application/json" } }
      );
      return;
    } catch {}
    try {
      await axiosInstance.patch(
        `/jobs/${encodeURIComponent(jobId)}`,
        { location_id: Number(locId) },
        { baseURL: "/api", headers: { "Content-Type": "application/json" } }
      );
      return;
    } catch {}
    try {
      await axiosInstance.post(
        `/jobs/${encodeURIComponent(jobId)}/location`,
        { location_id: Number(locId) },
        { baseURL: "/api", headers: { "Content-Type": "application/json" } }
      );
    } catch {}
  };

  const createJob = async () => {
    setBusy(true);
    setErrorText("");
    setOkMsg("");

    if (jobRefCheck.duplicate) {
      setBusy(false);
      setErrorText(jobRefCheck.msg || "Job reference already in use.");
      return;
    }

    if (form.use_week_commencing && !form.week_commencing) {
      setBusy(false);
      setErrorText("Week commencing is required when week commencing mode is enabled.");
      return;
    }

    if (form.use_week_commencing && form.week_commencing) {
      const selectedWeek = new Date(`${form.week_commencing}T12:00:00`);
      if (Number.isNaN(selectedWeek.getTime()) || selectedWeek.getDay() !== 1) {
        setBusy(false);
        setErrorText("Week Commencing must be a Monday.");
        return;
      }
    }

    if (containsWorkPackages && !workPackageDrafts.length) {
      setBusy(false);
      setErrorText("Add at least one Work Package or untick Job contains Work Packages.");
      return;
    }

    if (containsWorkPackages && workPackageDrafts.some((row) => !row.binding)) {
      setBusy(false);
      setErrorText("Every Work Package must resolve to a published workflow binding before the Job can be created.");
      return;
    }

    const job_ref = (form.job_ref || "").trim() || `JOB-${Date.now().toString().slice(-6)}`;

    try {
      const payload = {
        job_ref,
        customer_id: Number(form.customer_id),
        contact_id: form.contact_id ? Number(form.contact_id) : null,
        opportunity_id: form.opportunity_id ? Number(form.opportunity_id) : null,
        quote_id: form.quote_id ? Number(form.quote_id) : null,
        job_type_id: form.job_type_id ? Number(form.job_type_id) : null,
        job_subtype_id: form.job_subtype_id ? Number(form.job_subtype_id) : null,
        job_subtype2_id: form.job_subtype2_id ? Number(form.job_subtype2_id) : null,
        priority: form.priority || "Normal",
        week_commencing: form.use_week_commencing ? form.week_commencing || null : null,
        location_id: form.location_id ? Number(form.location_id) : null,
        ...(form.tags?.length ? { tags: form.tags.join(",") } : {}),
        notes: form.notes || null,
      };

      const res = await axiosInstance.post("/jobs/", payload, {
        baseURL: "/api",
        headers: { "Content-Type": "application/json" },
      });

      const newJobId = res?.data?.id;

      if (newJobId && form.location_id) {
        await ensureLocationPersisted(newJobId, form.location_id);
      }

      if (newJobId) {
        await createAssignmentsIfProvided(newJobId);
      }
      if (newJobId && containsWorkPackages && workPackageDrafts.length) {
        for (const draft of workPackageDrafts) {
          const wpPayload = {
            title: draft.title,
            package_type: draft.package_type || "work_package",
            work_package_type_id: Number(draft.work_package_type_id),
            work_package_subtype_id: draft.work_package_subtype_id ? Number(draft.work_package_subtype_id) : null,
            work_package_subtype2_id: draft.work_package_subtype2_id ? Number(draft.work_package_subtype2_id) : null,
            location_id: draft.location_id ? Number(draft.location_id) : null,
            assigned_resource_id: draft.assigned_resource_id ? Number(draft.assigned_resource_id) : null,
            week_commencing: form.use_week_commencing ? form.week_commencing || null : null,
            planned_start: form.use_week_commencing ? null : form.planned_start || null,
            planned_end: form.use_week_commencing ? null : form.planned_end || null,
            is_required: true,
          };
          const wpRes = await axiosInstance.post(`/jobs/${newJobId}/work-packages`, wpPayload, { baseURL: "/api" });
          await axiosInstance.post(`/jobs/${newJobId}/work-packages/${wpRes.data.id}/workflow/instantiate`, { force_template_version_id: draft.binding?.workflow_template_version_id || null }, { baseURL: "/api" });
        }
      }

      if (newJobId && expenseDrafts.length) {
        try {
          const toPost = expenseDrafts.map((p) => ({
            ...p,
            job_id: Number(newJobId),
            customer_id:
              typeof p.customer_id !== "undefined" && p.customer_id !== null
                ? p.customer_id
                : form.customer_id
                ? Number(form.customer_id)
                : null,
            resource_id:
              typeof p.resource_id === "number"
                ? p.resource_id
                : !form.use_week_commencing && form.resource_id
                ? Number(form.resource_id)
                : null,
          }));
          await Promise.allSettled(
            toPost.map((row) =>
              axiosInstance.post("/expenses", row, {
                baseURL: "/api",
                headers: { "Content-Type": "application/json" },
              })
            )
          );
        } catch {}
      }

      setOkMsg(`Job created (ID ${newJobId ?? "?"})`);
      alert(`✅ Job created: ${res?.data?.job_ref || job_ref}`);
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
      resetAll();
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      let msg = e?.message || "Failed to create job.";
      if (status === 422 && data) msg = formatValidation(data);
      if (status && data?.detail && typeof data.detail === "string") msg = data.detail;
      if (String(msg).toLowerCase().includes("duplicate") || status === 500) {
        msg = "Job reference already exists or backend rejected the value.";
      }
      setErrorText(String(msg));
    } finally {
      setBusy(false);
    }
  };

  // --- Location creation modal wiring ---
  const [locModalOpen, setLocModalOpen] = useState(false);
  const handleLocationCreated = (loc) => {
    setLocations((rows) => [{ ...loc }, ...(rows || [])]);
    setForm((f) => ({ ...f, location_id: String(loc.id) }));
  };

  // UI bits
  const jobRefBorder =
    jobRefCheck.duplicate
      ? "border-red-500 focus:ring-red-300"
      : jobRefCheck.status === "checking"
      ? "border-amber-400"
      : "border-gray-300";

  return (
    <div ref={rootRef} className={cardCls}>
      <HeaderBar
        title="Create Job"
        right={<StepDots total={TOTAL_STEPS} current={step} theme={theme} />}
      />

      <div className="p-3 md:p-5 min-w-0">
        {/* STEP 0 */}
        {step === 0 && (
          <div>
            <div className="text-[1.05rem] font-bold mb-1">Basics</div>
            <div className="text-gray-500 text-sm mb-3">Job identity and client</div>

            <label className="mb-4 flex items-center gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5" checked={containsWorkPackages} onChange={(e) => { setContainsWorkPackages(e.target.checked); if (!e.target.checked) setWorkPackageDrafts([]); }} />
              <span><span className="font-semibold block">Job contains Work Packages</span><span className="text-xs text-gray-500">Work Packages may remain unassigned during workforce planning.</span></span>
            </label>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className={fieldLabelCls}>Customer</label>
                <select
                  className={selectCls}
                  value={form.customer_id}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                >
                  <option value="">-- Select Customer --</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={fieldLabelCls}>Contact (optional)</label>
                <select
                  className={selectCls}
                  value={form.contact_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, contact_id: e.target.value }))
                  }
                  disabled={!form.customer_id || !contacts.length}
                >
                  <option value="">-- Select Contact --</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {[c.first_name, c.last_name].filter(Boolean).join(" ") ||
                        c.email ||
                        `Contact ${c.id}`}
                    </option>
                  ))}
                </select>
                {!!form.customer_id && !contacts.length && (
                  <div className="text-xs text-gray-500 mt-1">
                    No contacts returned for this customer (API filtering/pagination issue).
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className={fieldLabelCls}>Opportunity (optional)</label>
                <select
                  className={selectCls}
                  value={form.opportunity_id}
                  onChange={(e) => handleOpportunityChange(e.target.value)}
                  disabled={!form.customer_id || commercialLoading}
                >
                  <option value="">-- No Opportunity --</option>
                  {customerScopedOpportunities.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.title || o.name || `Opportunity #${o.id}`} — Opportunity #{o.id}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={fieldLabelCls}>Quote (optional)</label>
                <select
                  className={selectCls}
                  value={form.quote_id}
                  onChange={(e) => handleQuoteChange(e.target.value)}
                  disabled={!form.customer_id || commercialLoading}
                >
                  <option value="">-- No Quote --</option>
                  {customerScopedQuotes.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.title || q.quote_ref || `Quote #${q.id}`} — Quote #{q.id}
                      {q.status ? ` — ${q.status}` : ""}
                    </option>
                  ))}
                </select>
                {!!form.customer_id && !!form.opportunity_id && (
                  <div className="text-xs text-gray-500 mt-1">
                    Quotes are filtered to this customer and Opportunity.
                  </div>
                )}
              </div>
            </div>

            {commercialLoading && (
              <div className="text-xs text-gray-500 mt-2">
                Loading Opportunities and Quotes…
              </div>
            )}
            {commercialError && (
              <div className="text-xs text-amber-700 mt-2">{commercialError}</div>
            )}

            <div className="mt-3">
              <label className={fieldLabelCls}>Tags</label>
              <div className="flex flex-wrap gap-2">
                {(form.tags || []).map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2 py-0.5 min-h-[36px] md:min-h-0 rounded-full border border-gray-300 bg-gray-50 text-xs min-w-0"
                  >
                    {t}
                    <button
                      type="button"
                      className="text-gray-600 hover:text-black w-8 h-8 md:w-auto md:h-auto inline-flex items-center justify-center rounded-full"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          tags: (f.tags || []).filter((x) => x !== t),
                        }))
                      }
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  className={inputCls}
                  placeholder="Add tag and press Enter"
                  onKeyDown={(e) => {
                    const v = e.currentTarget.value.trim();
                    if (e.key === "Enter" && v) {
                      setForm((f) => ({
                        ...f,
                        tags: Array.from(new Set([...(f.tags || []), v])),
                      }));
                      e.currentTarget.value = "";
                    }
                  }}
                />
              </div>
            </div>

            <div className="mt-3">
              <label className={fieldLabelCls}>Job Reference (optional)</label>
              <input
                className={`${inputCls} ${jobRefBorder}`}
                placeholder="e.g., ANP-2025-00123"
                value={form.job_ref}
                onChange={(e) =>
                  setForm((f) => ({ ...f, job_ref: e.target.value }))
                }
                onBlur={() =>
                  setForm((f) => ({ ...f, job_ref: (f.job_ref || "").trim() }))
                }
              />
              {jobRefCheck.status === "checking" && (
                <div className="text-xs text-amber-600 mt-1">Checking…</div>
              )}
              {!!(!jobRefCheck.duplicate && jobRefCheck.status === "unknown") && (
                <div className="text-xs text-gray-500 mt-1">
                  Couldn’t verify uniqueness right now; you can still proceed.
                </div>
              )}
              {jobRefCheck.duplicate && (
                <div className="text-xs text-red-600 mt-1">
                  {jobRefCheck.msg || "Job ref already exists."}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <div>
            <div className="text-[1.05rem] font-bold mb-3">Taxonomy</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className={fieldLabelCls}>Job Type</label>
                <select
                  className={selectCls}
                  value={form.job_type_id}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      job_type_id: e.target.value,
                      job_subtype_id: "",
                      job_subtype2_id: "",
                    }))
                  }
                >
                  <option value="">-- Select Type --</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={fieldLabelCls}>Subtype</label>
                <select
                  className={selectCls}
                  value={form.job_subtype_id}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      job_subtype_id: e.target.value,
                      job_subtype2_id: "",
                    }))
                  }
                  disabled={!form.job_type_id}
                >
                  <option value="">-- Select Subtype --</option>
                  {subtypes.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={fieldLabelCls}>Subtype 2</label>
                <select
                  className={selectCls}
                  value={form.job_subtype2_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, job_subtype2_id: e.target.value }))
                  }
                  disabled={!form.job_subtype_id}
                >
                  <option value="">-- Select Subtype 2 --</option>
                  {subtypes2.map((s2) => (
                    <option key={s2.id} value={s2.id}>
                      {s2.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {step === 1 && containsWorkPackages && (
          <div className="mt-5">
            <WorkPackageComposer
              jobTypeName={types.find((t) => String(t.id) === String(form.job_type_id))?.name || ""}
              resources={resources}
              value={workPackageDrafts}
              onChange={setWorkPackageDrafts}
            />
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div>
            <div className="text-[1.05rem] font-bold mb-3">
              Schedule &amp; Resourcing
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                  <label className="flex items-center gap-3 min-h-[44px] text-sm font-semibold text-gray-900 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-5 h-5 shrink-0"
                      checked={!!form.use_week_commencing}
                      onChange={(e) => onUseWeekCommencingChange(e.target.checked)}
                    />
                    Use Week Commencing instead of specific start and end dates
                  </label>
                  <div className="text-xs text-gray-500 mt-1">
                    Use this when the job is planned for a week window rather than a fixed slot.
                  </div>
                </div>
              </div>

              <div>
                <label className={fieldLabelCls}>Priority</label>
                <select
                  className={selectCls}
                  value={form.priority}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, priority: e.target.value }))
                  }
                >
                  <option>Low</option>
                  <option>Normal</option>
                  <option>High</option>
                  <option>Urgent</option>
                </select>
              </div>

              {/* Primary Resource */}
              <div>
                <label className={fieldLabelCls}>Primary Resource (optional)</label>
                <select
                  className={`${selectCls} ${form.use_week_commencing ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
                  value={form.resource_id}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      resource_id: e.target.value,
                      co_resource_id:
                        f.co_resource_id === e.target.value ? "" : f.co_resource_id,
                    }))
                  }
                  disabled={form.use_week_commencing}
                >
                  <option value="">-- Unassigned --</option>
                  {sortedResources.map((r) => (
                    <option key={r.id} value={r.id}>
                      {friendlyResourceName(r)}
                    </option>
                  ))}
                </select>
                {form.use_week_commencing && (
                  <div className="text-xs text-gray-500 mt-1">
                    Disabled in week commencing mode. Allocate later in planner when exact timing is known.
                  </div>
                )}
              </div>

              {/* Co-Resource (single optional) */}
              <div>
                <label className={fieldLabelCls}>Co-Resource (optional)</label>
                <select
                  className={`${selectCls} ${form.use_week_commencing ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
                  value={form.co_resource_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, co_resource_id: e.target.value }))
                  }
                  disabled={form.use_week_commencing}
                >
                  <option value="">-- None --</option>
                  {sortedResources
                    .filter((r) => String(r.id) !== String(form.resource_id || ""))
                    .map((r) => (
                      <option key={r.id} value={String(r.id)}>
                        {friendlyResourceName(r)}
                      </option>
                    ))}
                </select>
                {form.use_week_commencing && (
                  <div className="text-xs text-gray-500 mt-1">
                    Disabled in week commencing mode.
                  </div>
                )}
              </div>

              {/* Location */}
              <div className="md:col-span-2">
                <label className={fieldLabelCls}>Location (optional)</label>
                <div className="grid md:grid-cols-[1fr_auto] gap-2">
                  <select
                    className={selectCls}
                    value={form.location_id}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, location_id: e.target.value }))
                    }
                  >
                    <option value="">-- Not set --</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name || l.address_line_1 || l.postcode || `Location ${l.id}`}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => setLocModalOpen(true)}
                    className="px-3 py-2 min-h-[44px] w-full md:w-auto rounded font-semibold text-white bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick"
                    title="Create a new Location"
                  >
                    Create Location
                  </button>
                </div>

                <LocationEditModal
                  open={locModalOpen}
                  onClose={() => setLocModalOpen(false)}
                  onCreated={handleLocationCreated}
                  initial={{}}
                />
              </div>

              {form.use_week_commencing ? (
                <div className="md:col-span-2">
                  <label className={fieldLabelCls}>Week Commencing</label>
                  <input
                    type="date"
                    className={selectCls}
                    value={form.week_commencing}
                    onChange={(e) => setForm((f) => ({ ...f, week_commencing: e.target.value }))}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Select the Monday for the job week. Historic and future weeks are supported when exact working dates are not known.
                  </div>
                </div>
              ) : (
                <>
                  {/* Planned Start (custom: date + quarter-hour time) */}
                  <div>
                    <label className={fieldLabelCls}>Planned Start</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="date"
                        className={inputCls}
                        value={startDate}
                        onChange={(e) => onStartDateChange(e.target.value)}
                      />
                      <select
                        className={selectCls}
                        value={startTime}
                        onChange={(e) => onStartTimeChange(e.target.value)}
                      >
                        {quarterTimes.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Planned End (custom: date + quarter-hour time) */}
                  <div>
                    <label className={fieldLabelCls}>Planned End</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="date"
                        className={inputCls}
                        value={endDate}
                        onChange={(e) => onEndDateChange(e.target.value)}
                      />
                      <select
                        className={selectCls}
                        value={endTime}
                        onChange={(e) => onEndTimeChange(e.target.value)}
                      >
                        {quarterTimes.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div>
            <div className="text-[1.05rem] font-bold mb-2">Documents</div>
            <p className="text-gray-500 text-sm mb-3">
              Upload job/customer documents now.
            </p>

            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className={fieldLabelCls}>Customer Ref (optional override)</label>
                <input
                  className={inputCls}
                  placeholder="e.g., CUST-0001"
                  value={customerRef}
                  onChange={(e) => setCustomerRef(e.target.value)}
                />
              </div>

              <div>
                <label className={fieldLabelCls}>Job Ref (optional)</label>
                <input
                  className={`${inputCls} ${jobRefBorder}`}
                  placeholder="e.g., JOB-123456"
                  value={form.job_ref}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, job_ref: e.target.value }))
                  }
                  onBlur={() =>
                    setForm((f) => ({ ...f, job_ref: (f.job_ref || "").trim() }))
                  }
                />
              </div>
            </div>

            <div className="mb-3">
              <label className={fieldLabelCls}>Document Tags</label>
              <div className="flex flex-wrap gap-2">
                {(docTags || []).map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2 py-0.5 min-h-[36px] md:min-h-0 rounded-full border border-gray-300 bg-gray-50 text-xs min-w-0"
                  >
                    {t}
                    <button
                      type="button"
                      className="text-gray-600 hover:text-black w-8 h-8 md:w-auto md:h-auto inline-flex items-center justify-center rounded-full"
                      onClick={() => setDocTags((arr) => arr.filter((x) => x !== t))}
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  className={inputCls}
                  placeholder="Add tag and press Enter"
                  onKeyDown={(e) => {
                    const v = e.currentTarget.value.trim();
                    if (e.key === "Enter" && v) {
                      setDocTags((arr) => Array.from(new Set([...(arr || []), v])));
                      e.currentTarget.value = "";
                    }
                  }}
                />
              </div>
            </div>

            <DocumentUploadAccordion
              customerId={form.customer_id || ""}
              jobId={""}
              customerRef={customerRef}
              jobRef={form.job_ref || ""}
              defaultTagsCsv={(docTags || []).join(",")}
              defaultPublish={false}
            />

            <div className="mt-6">
              <div className="text-[1.05rem] font-bold mb-2">Expenses</div>
              <ExpenseCreateAccordion
                title="Add Expenses for this job (queued until create)"
                customerId={form.customer_id || null}
                defaultResourceId={!form.use_week_commencing ? form.resource_id || "" : ""}
                onDraftsChange={setExpenseDrafts}
                accent={theme?.accent_color || theme?.header_bg || "#0e2a3f"}
              />
            </div>
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div>
            <div className="text-[1.05rem] font-bold mb-2">Notes</div>
            <textarea
              className={areaCls}
              placeholder="Any key context or instructions for this job…"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
        )}

        {/* STEP 5 */}
        {step === 5 && (
          <div>
            <div className="text-[1.05rem] font-bold mb-2">Review</div>
            <div className="border border-gray-200 rounded p-3 text-sm">
              <dl className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-x-4 gap-y-1 md:gap-y-2 [&>dt]:mt-2 md:[&>dt]:mt-0 [&>dd]:min-w-0 [&>dd]:break-words [&>dd]:mb-2 md:[&>dd]:mb-0">
                <dt className="text-gray-600">Customer</dt>
                <dd>
                  {customers.find((c) => String(c.id) === String(form.customer_id))?.name || "-"}
                </dd>

                <dt className="text-gray-600">Contact</dt>
                <dd>
                  {(() => {
                    const c = contacts.find((x) => String(x.id) === String(form.contact_id));
                    return c ? [c.first_name, c.last_name].filter(Boolean).join(" ") : "-";
                  })()}
                </dd>

                <dt className="text-gray-600">Opportunity</dt>
                <dd>
                  {form.opportunity_id
                    ? (() => {
                        const o = opportunities.find(
                          (x) => String(x.id) === String(form.opportunity_id)
                        );
                        return o
                          ? `${o.title || o.name || "Opportunity"} — #${o.id}`
                          : `Opportunity #${form.opportunity_id}`;
                      })()
                    : "-"}
                </dd>

                <dt className="text-gray-600">Quote</dt>
                <dd>
                  {form.quote_id
                    ? (() => {
                        const q = quotes.find(
                          (x) => String(x.id) === String(form.quote_id)
                        );
                        return q
                          ? `${q.title || q.quote_ref || "Quote"} — #${q.id}`
                          : `Quote #${form.quote_id}`;
                      })()
                    : "-"}
                </dd>

                <dt className="text-gray-600">Type / Subtypes</dt>
                <dd>
                  {[
                    (types.find((t) => String(t.id) === String(form.job_type_id)) || {}).name,
                    (subtypes.find((s) => String(s.id) === String(form.job_subtype_id)) || {}).name,
                    (subtypes2.find((s2) => String(s2.id) === String(form.job_subtype2_id)) || {}).name,
                  ]
                    .filter(Boolean)
                    .join(" → ") || "-"}
                </dd>

                <dt className="text-gray-600">Work Packages</dt>
                <dd>{containsWorkPackages ? (workPackageDrafts.length ? workPackageDrafts.map(w=>w.title).join(", ") : "Enabled — none selected") : "No"}</dd>

                <dt className="text-gray-600">Priority</dt>
                <dd>{form.priority || "-"}</dd>

                <dt className="text-gray-600">Planning Mode</dt>
                <dd>{form.use_week_commencing ? "Week Commencing" : "Exact Dates"}</dd>

                <dt className="text-gray-600">Planned</dt>
                <dd>
                  {form.use_week_commencing
                    ? form.week_commencing || "-"
                    : `${form.planned_start || "-"} → ${form.planned_end || "-"}`}
                </dd>

                <dt className="text-gray-600">Resources</dt>
                <dd>
                  {form.use_week_commencing
                    ? "-"
                    : (() => {
                        const ids = [form.resource_id, form.co_resource_id].filter(Boolean).map(String);
                        if (!ids.length) return "-";
                        const labelFor = (id) => friendlyResourceName(resources.find((r) => String(r.id) === id));
                        const labels = ids.map(labelFor).filter(Boolean);
                        return labels.length ? labels.join(", ") : "-";
                      })()}
                </dd>

                <dt className="text-gray-600">Location</dt>
                <dd>
                  {form.location_id
                    ? (locations.find((l) => String(l.id) === String(form.location_id))?.name ||
                      `Location #${form.location_id}`)
                    : "-"}
                </dd>

                <dt className="text-gray-600">Tags</dt>
                <dd>{(form.tags || []).join(", ") || "-"}</dd>

                <dt className="text-gray-600">Job Ref</dt>
                <dd>{form.job_ref || "(auto on create)"}</dd>

                <dt className="text-gray-600">Customer Ref</dt>
                <dd>{customerRef || "-"}</dd>

                <dt className="text-gray-600">Doc Tags</dt>
                <dd>{(docTags || []).join(", ") || "-"}</dd>

                <dt className="text-gray-600">Queued Expenses</dt>
                <dd>
                  {expenseDrafts.length ? (
                    <div className="space-y-1">
                      <div>
                        {expenseDrafts.length} item(s) — £
                        {expenseDrafts.reduce((s, d) => s + (Number(d.amount) || 0), 0).toFixed(2)}
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-500">None</span>
                  )}
                </dd>
              </dl>
            </div>
          </div>
        )}

        {errorText && <div className="mt-3 text-red-700 font-semibold break-words">❌ {errorText}</div>}
        {okMsg && <div className="mt-3 text-emerald-700 font-semibold break-words">✅ {okMsg}</div>}
      </div>

      {/* Footer actions */}
      <div className="px-3 md:px-4 py-3 border-t border-gray-200 flex flex-col md:flex-row items-stretch md:items-center gap-2 md:justify-between">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className={`px-3 py-2 min-h-[44px] w-full md:w-auto justify-center rounded font-semibold ${
            step === 0
              ? "bg-gray-200 text-gray-700 opacity-60 cursor-not-allowed"
              : "bg-gray-200 text-gray-900 hover:bg-gray-300"
          } flex items-center gap-2`}
        >
          <FaChevronLeft /> Back
        </button>

        <div className="grid grid-cols-2 md:flex md:items-center gap-2 w-full md:w-auto">
          <button
            type="button"
            onClick={resetAll}
            className="px-3 py-2 min-h-[44px] w-full rounded font-semibold bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white"
          >
            Reset
          </button>

          {step < TOTAL_STEPS - 1 ? (
            <button
              type="button"
              disabled={!canNext}
              onClick={() => setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1))}
              className={`px-3 py-2 min-h-[44px] w-full justify-center rounded font-semibold text-white ${
                canNext
                  ? "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick"
                  : "bg-gray-400 cursor-not-allowed"
              } flex items-center gap-2`}
            >
              Next <FaChevronRight />
            </button>
          ) : (
            <button
              type="button"
              disabled={busy || jobRefCheck.duplicate}
              onClick={createJob}
              className={`px-3 py-2 min-h-[44px] w-full md:min-w-[120px] rounded font-semibold text-white ${
                busy || jobRefCheck.duplicate
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick"
              }`}
              title={jobRefCheck.duplicate ? "Fix duplicate Job Ref first" : ""}
            >
              {busy ? "Creating…" : "Create Job"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobFormWidget;
